import * as _ from "lodash";
import { IItemRecord } from "records/item/item";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import type { IScope } from "reducers/scope";
import { INamespace } from "reducers/products";
import me from "models/me";
import { getOrder } from "components/build-order";
import { ICart } from "constants/cart";
import { IOrder } from "records/order";
import { PRODUCTS_FILTER } from "constants/brand-settings";

export interface IArgs {
    productsMap: { [id: string]: IItemRecord };
    cart: ICart;
    onFetch: (filters: { [key: string]: any }, cacheKey?: string) => void;

    id?: string;
    initialValue?: string;
    collection?: Partial<INamespace>;
    shouldFetch?: boolean;
    fields?: string;
    scope?: IScope;
}

export interface IReturn {
    product: IItemRecord | null;
    productId: string;
    setProductId: Dispatch<SetStateAction<string>>;
}

export const useFetchProductById = (args: IArgs): IReturn => {
    const {
        productsMap = {},
        onFetch,
        shouldFetch = true,
        id,
        initialValue = "",
        fields = "__all",
        scope,
        collection,
        cart,
    } = args;
    const order: IOrder = useMemo(() => getOrder(cart), [cart]);
    const [productId, setProductId] = useState(initialValue);
    const [isRequestSent, setIsRequestSent] = useState(false);
    const companyId = useMemo<string | undefined>(() => scope?.viewAs?._id, [scope]);
    const viewAsGuest = useMemo<boolean>(() => !!scope?.viewAsGuest, [scope]);
    const product = useMemo<IItemRecord | null>(() => {
        if (collection && !collection.results?.includes(productId)) {
            return null;
        }
        return _.get(productsMap, productId, null);
    }, [productsMap, collection, productId]);

    const useOrderWarehouseFiltering = useMemo(
        () =>
            scope.brand.getPreference("set_warehouse_for_linesheets") &&
            scope.brand.getPreference("filter_products_by") === PRODUCTS_FILTER.BY_WAREHOUSE_IN_WO,
        [scope.brand]
    );

    const cacheKey = useMemo(() => generatePageProductsCacheKey(scope), [scope]);
    const request = useMemo(() => {
        const params: any = {
            _id: productId,
            __fields: fields,
            __populate: "__variant",
            __company: companyId,
            __guest: viewAsGuest,
            __active_pricesheet: me._activePriceTemplate,
        };
        if (useOrderWarehouseFiltering && order?.selected_warehouse) {
            params.__warehouses = [order.selected_warehouse];
        }
        return params;
    }, [productId, fields, companyId, viewAsGuest, useOrderWarehouseFiltering, order?.selected_warehouse]);
    const handleFetchProduct = useCallback(() => onFetch?.(request, cacheKey), [onFetch, request, cacheKey]);

    useEffect(() => {
        if (id !== null && id !== undefined) {
            setProductId(id);
        }
    }, [id]);

    useEffect(() => {
        setIsRequestSent(false);
    }, [viewAsGuest, companyId, productId]);

    useEffect(() => {
        if (!productId || (isRequestSent && product?._id === productId)) {
            return;
        }

        if (shouldFetch && !isRequestSent) {
            setIsRequestSent(true);
            handleFetchProduct();
        }
    }, [productId, shouldFetch, product, handleFetchProduct, isRequestSent]);

    return { product, productId, setProductId };
};

export const generatePageProductsCacheKey = (scope: IScope): string => `${scope?.viewAs?._id}-${!!scope?.viewAsGuest}`;
